import React from 'react';
import { connect } from 'react-redux';
import { get, orderBy, template } from 'lodash';
import { Grid, Row } from 'react-bootstrap';

import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchChannel, fetchChannelCallouts } from 'services/channel/duck';
import { fetchSemCampaignSummary, fetchSemConversionSummary, fetchSemKeywordSummary, fetchSemAdgroupSummary } from 'services/sem/duck';
import { fetchLayerTwoSEMSummary } from 'services/layerTwo/duck';
import { formatPercentage } from 'helpers/formatNumbers';
import { reduceLayerTwoData } from 'helpers/data';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { insertBeforeItem } from 'helpers/utilities';
import { prefixKeywordsWithSpace } from 'helpers/strings';

// Components and Containers
import { LayerTwoDashboardComponent } from 'containers/dashboards/DashboardBaseComponent';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ExportPDF from 'containers/buttons/ExportPDF';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';
import SubModule from 'components/organisms/SubModule';

// Charts
import BarChart from 'components/molecules/charts/BarChart';
import LineChart from 'components/molecules/charts/LineChart';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';

// Tables
import CommonSummaryTable from 'components/molecules/tables/CommonSummaryTable';

// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';

function percentageToolTipFormatter(format) {
    return template(format, {
        interpolate: /{([\s\S]+?)}/g
    })({
        series: this.series,
        point: {
            ...this,
            y: formatPercentage(this.y)
        }
    });
}

function percentageFormatter() {
    return formatPercentage(this.value);
}

class LayerTwoSEMContainer extends LayerTwoDashboardComponent {
    constructor(props) {
        super(props, 'sem');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();
        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchSemKeywordSummary(payload);
        this.props.fetchSemAdgroupSummary(payload);
        this.props.fetchSemCampaignSummary(payload);
        this.props.fetchLayerTwoSEMSummary(payload);
        this.props.fetchSemConversionSummary(payload);
    }

    render() {
        const stub = `l2-${this.getCurrentChannel()}`;
        const channel = this.getCurrentChannel();
        const {
            backfillBuckets,
            xAxisLabel,
            selectedTimeRange,
            entityObjective,
            entityObjectiveLabelCode,
            showConversionRevenue,
            showSemCpconv
        } = this.props;
        const {
            conversionTermSingular,
            conversionTermPlural,
            isLabelCoded
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const storeData = this.props.layerTwo[channel];
        const hasLayerTwoSummaryData = get(storeData, 'summary.hasData', false);

        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(storeData.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }

        const sortedChildSummaryTableData =
            hasLayerTwoSummaryData ? orderBy(childSummaryTableData, ['clicks'], ['desc']) : [];

        const hasLayerThreeChannelData = get(this.props, 'layerThreeChannel.hasData', false);
        const hasCalloutData = get(this.props, 'layerThreeChannel.callouts.params.hasData', false);
        const hasCampaignSummaryData = get(this.props, 'campaignSummary.hasData', false);
        const hasAdgroupSummaryData = get(this.props, 'adgroupSummary.hasData', false);
        const hasKeywordSummaryData = get(this.props, 'keywordSummary.hasData', false);
        const hasConversionSummaryData = get(this.props, 'conversionSummary.hasData', false);

        const callouts = get(this.props, 'layerThreeChannel.callouts.data', {});
        const calloutWindow = get(this.props, 'layerThreeChannel.callouts.params.timeRanges.previous.windowSize', null);
        const layerThreeChannelData = get(this.props, 'layerThreeChannel.data', {});
        const keywordSummaryData = prefixKeywordsWithSpace(get(this.props, 'keywordSummary.data', []));
        const userId = get(this.props, 'currentUser.id', null);

        const sortedKeywords =
            orderBy(keywordSummaryData, ['clicks'], ['desc']);

        const rawCampaignSummaryData = get(this.props, 'campaignSummary.data', []);
        const campaignSummaryData = rawCampaignSummaryData.filter(val => val.advertising_channel_type !== 'Display');
        const sortedCampaignSummaryData =
            hasCampaignSummaryData ? orderBy(campaignSummaryData, ['clicks'], ['desc']) : [];


        const rawAdgroupSummaryData = get(this.props, 'adgroupSummary.data', []);
        const adgroupSummaryData = rawAdgroupSummaryData.filter(val => val.advertising_channel_sub_type !== 'Gmail Ad campaign'
            && val.advertising_channel_type !== 'Display');
        const sortedAdgroupSummaryData =
            hasAdgroupSummaryData ? orderBy(adgroupSummaryData, ['clicks'], ['desc']) : [];
        const gmailSummaryData = rawAdgroupSummaryData.filter(val => val.advertising_channel_sub_type === 'Gmail Ad campaign');
        const hasGmailSummaryData = Boolean(gmailSummaryData.length);
        const sortedGmailSummaryData =
            hasGmailSummaryData ? orderBy(gmailSummaryData, ['gmail_secondary_clicks'], ['desc']) : [];

        const gdnAdgroupSummaryData = orderBy(
            rawAdgroupSummaryData.filter(val => val.advertising_channel_type === 'Display'),
            ['clicks'], ['desc']
        );
        const hasGdnAdgroupSummaryData = Boolean(gdnAdgroupSummaryData.length);

        const conversionSummaryData = get(this.props, 'conversionSummary.data', []);
        const sortedConversionSummaryData =
            hasConversionSummaryData ? orderBy(conversionSummaryData, ['conversions'], ['desc']) : [];
        const showConversionBreakdown = conversionSummaryData.length > 1;

        const calloutSettings = [
            {
                key: 'clicks',
                title: 'Clicks',
                format: '0,0'
            },
            {
                key: 'ctr',
                title: 'Click Through Rate',
                format: '0.00%'
            },
            {
                key: 'cpc',
                title: 'Cost per Click',
                format: '$0.00'
            },
            {
                key: 'conversion_rate',
                title: `${conversionTermSingular} Rate`,
                format: '0.00%'
            },
            showSemCpconv ? {
                key: 'conversions',
                title: conversionTermPlural,
                format: '0,0'
            } : null,
            showSemCpconv ? {
                key: 'cpconv',
                title: `Cost per ${conversionTermSingular}`,
                format: '$0.00'
            } : null
        ].filter(e => e);

        const mdSizeOverride = calloutSettings.length > 4 ? 4 : undefined;
        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, mdSizeOverride, isLabelCoded
        });

        const isChannelAvailable = get(this.props, 'entityMeta.channelAvailability.sem', false);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');

        let layerTwoSummaryAccessors = ['impressions', 'clicks', 'ctr', 'cpc', 'spend', 'conversions'];

        if (showSemCpconv && !layerTwoSummaryAccessors.includes('cpconv')) {
            layerTwoSummaryAccessors = insertBeforeItem(layerTwoSummaryAccessors, 'spend', 'cpconv');
        }

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    SEM
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs     */}
                <Row>
                    {calloutComponents}
                </Row>

                {/* Child Charts & Tables */}
                {hasLayerTwoSummaryData ? (
                    <TwoAxisBarWithScatterChart
                        title={`TOP PERFORMANCE BY ${aliasDescriptor}`}
                        exportName={`${stub}-char-performance`}
                        data={sortedChildSummaryTableData}
                        showTopOnly
                        yBar="clicks"
                        scatter="cpc"
                        scatterTitle="CPC"
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yScatterOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        }}
                        yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        categories={sortedChildSummaryTableData.map(c => c.entity_name)} />
                ) : (
                    <RenderingTile height='400px' show={hasLayerTwoSummaryData} />
                )}

                {hasLayerTwoSummaryData ? (
                    <LayerTwoSummaryTable
                        title={`PERFORMANCE BY ${aliasDescriptor}`}
                        channel={this.getCurrentChannel()}
                        exportName={`${stub}-table-performance`}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        showConversionRevenue={showConversionRevenue}
                        showSemCpconv={showSemCpconv}
                        accessors={layerTwoSummaryAccessors}
                        cacheKey={`${stub}-table-performance-${userId}-${this.getCurrentEntityId()}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasLayerTwoSummaryData} />
                )}

                {/* Charts */}
                {hasLayerThreeChannelData ? (
                    <TwoAxisBarWithLineChart
                        title={`CLICKS & ${conversionTermPlural.toUpperCase()}`}
                        exportName={`${stub}-clicks-&-conversions`}
                        data={layerThreeChannelData}
                        yBar="clicks"
                        yLine="conversions"
                        yLineTitle={conversionTermPlural}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreeChannelData} />
                )}

                {hasLayerThreeChannelData ? (
                    <LineChart
                        title={`${conversionTermSingular.toUpperCase()} RATE TREND`}
                        exportName={`${stub}-conversion-rate-trend`}
                        data={{ [this.state.channel]: layerThreeChannelData }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yTitle={`${conversionTermSingular} Rate`}
                        yOptions={{
                            tooltip: {
                                pointFormatter: percentageToolTipFormatter
                            }
                        }}
                        yAxisOptions={{
                            labels: {
                                formatter: percentageFormatter
                            }
                        }}
                        y="conversion_rate"
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreeChannelData} />
                )}

                {hasConversionSummaryData && showConversionBreakdown ? (
                    <BarChart
                        title={`TOP SEARCH ${conversionTermPlural.toUpperCase()}`}
                        exportName={`${stub}-conversion-breakdown`}
                        data={sortedConversionSummaryData}
                        showTopOnly
                        y="conversions"
                        yAxisLabel="Conversions"
                        yAxisLabel={conversionTermPlural}
                        color={this.props.theme.orderedColors[0]}
                        animateDuration={2000}
                        categories={sortedConversionSummaryData.map(c => c.conversion_name)} />
                ) : (
                    <RenderingTile height='400px' show={hasConversionSummaryData && showConversionBreakdown} />
                )}

                <CommonSummaryTable
                    title={`${conversionTermSingular.toUpperCase()} SUMMARY`}
                    exportName={`${stub}-conversion-summary`}
                    keyField="conversionName"
                    defaultSorted={[{
                        id: 'conversions',
                        desc: true
                    }]}
                    hasData={hasConversionSummaryData}
                    conversionTermSingular={conversionTermSingular}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={['conversionName', 'conversions']}
                    data={sortedConversionSummaryData}
                />
                {!!campaignSummaryData.length &&
                    <SubModule title='By Search Campaign' hasData={hasCampaignSummaryData}>
                        {hasCampaignSummaryData ? (
                            <BarChart
                                title="TOP CLICKS BY SEARCH CAMPAIGN"
                                exportName={`${stub}-clicks-by-search-campaign`}
                                data={sortedCampaignSummaryData}
                                showTopOnly
                                y="clicks"
                                yAxisLabel="Clicks"
                                color={this.props.theme.orderedColors[0]}
                                animateDuration={2000}
                                categories={sortedCampaignSummaryData.map(c => c.campaign_name)} />
                        ) : (
                            <RenderingTile height='400px' show={hasCampaignSummaryData} />
                        )}

                        <CommonSummaryTable
                            title="SEARCH CAMPAIGN SUMMARY"
                            exportName={`${stub}-search-campaign-summary`}
                            keyField="campaignName"
                            tableType="sem"
                            entityObjectiveLabelCode={entityObjectiveLabelCode}
                            showSemCpconv={showSemCpconv}
                            showConversionRevenue={showConversionRevenue}
                            hasData={hasCampaignSummaryData}
                            data={campaignSummaryData}
                        />
                    </SubModule>
                }
                <SubModule title='By Search Ad Group' hasData={hasAdgroupSummaryData ? adgroupSummaryData.length > 0 : hasAdgroupSummaryData}>
                    {hasAdgroupSummaryData ? (
                        <BarChart
                            title="TOP CLICKS BY SEARCH AD GROUP"
                            exportName={`${stub}-clicks-by-search-adgroup`}
                            data={sortedAdgroupSummaryData}
                            showTopOnly
                            y="clicks"
                            yAxisLabel="Clicks"
                            color={this.props.theme.orderedColors[0]}
                            animateDuration={2000}
                            categories={sortedAdgroupSummaryData.map(c => c.adgroup_name)} />
                    ) : (
                        <RenderingTile height='400px' show={hasCampaignSummaryData} />
                    )}

                    <CommonSummaryTable
                        title="SEARCH AD GROUP SUMMARY"
                        exportName={`${stub}-search-adgroup-summary`}
                        keyField="adgroupName"
                        tableType="sem"
                        showSemCpconv={showSemCpconv}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        hasData={hasAdgroupSummaryData}
                        data={adgroupSummaryData}
                    />
                </SubModule>

                <SubModule title='By Google Display Network (GDN) Ad Group' hasData={!hasGdnAdgroupSummaryData ? 'HIDE_SECTION' : hasGdnAdgroupSummaryData}>
                    {hasGdnAdgroupSummaryData ? (
                        <BarChart
                            title="TOP CLICKS BY GDN AD GROUP"
                            exportName={`${stub}-clicks-by-gdn-adgroup`}
                            data={gdnAdgroupSummaryData}
                            showTopOnly
                            y="clicks"
                            yAxisLabel="Clicks"
                            color={this.props.theme.orderedColors[0]}
                            animateDuration={2000}
                            categories={gdnAdgroupSummaryData.map(c => c.adgroup_name)} />
                    ) : (
                        <RenderingTile height='400px' show={hasGdnAdgroupSummaryData} />
                    )}

                    <CommonSummaryTable
                        title="GDN AD GROUP SUMMARY"
                        exportName={`${stub}-gdn-adgroup-summary`}
                        keyField="adgroupName"
                        tableType="sem"
                        showSemCpconv={showSemCpconv}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        hasData={hasGdnAdgroupSummaryData}
                        data={gdnAdgroupSummaryData}
                    />
                </SubModule>

                <SubModule title='By Keyword' hasData={hasKeywordSummaryData}>
                    {hasKeywordSummaryData ? (
                        <TwoAxisBarWithScatterChart
                            title="TOP KEYWORDS"
                            exportName={`${stub}-top-keywords`}
                            data={sortedKeywords}
                            showTopOnly
                            yBar="clicks"
                            scatter="cpc"
                            scatterTitle="CPC"
                            toolTipOptions={{
                                pointFormatter: toolTipPointFormatterGenerator
                            }}
                            yScatterOptions={{
                                labels: {
                                    formatter: labelFormatterGenerator('priceWithoutSpace')
                                }
                            }}
                            yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                            animateDuration={2000}
                            tactic={this.state.channel}
                            categories={sortedKeywords.map(k => k.keyword)} />
                    ) : (
                        <RenderingTile height='400px' show={hasKeywordSummaryData} />
                    )}

                    <CommonSummaryTable
                        title="KEYWORD SUMMARY"
                        exportName={`${stub}-keyword-summary`}
                        keyField="keyword"
                        tableType="sem"
                        showSemCpconv={showSemCpconv}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        hasData={hasKeywordSummaryData}
                        data={keywordSummaryData}
                    />
                </SubModule>

                <SubModule
                    title='Gmail'
                    hasData={hasGmailSummaryData === false ? 'HIDE_SECTION' : hasGmailSummaryData}
                >
                    {hasGmailSummaryData ? (
                        <TwoAxisBarWithScatterChart
                            title="TOP CLICKS TO SITE BY AD GROUP"
                            exportName={`${stub}-clicks-to-site-by-adgroup`}
                            data={sortedGmailSummaryData}
                            showTopOnly
                            yBar="gmail_secondary_clicks"
                            yBarTitle="Clicks to Site"
                            scatter="gmail_secondary_clicks_rate"
                            scatterTitle="Clicks to Site Rate"
                            toolTipOptions={{
                                pointFormatter: toolTipPointFormatterGenerator
                            }}
                            yScatterOptions={{
                                labels: {
                                    formatter: labelFormatterGenerator('percentage')
                                }
                            }}
                            yScatterUserOptions={{ formatByType: 'percentage' }}
                            animateDuration={2000}
                            categories={sortedGmailSummaryData.map(c => c.adgroup_name)} />
                    ) : (
                        <RenderingTile height='400px' show={hasGmailSummaryData} />
                    )}

                    <CommonSummaryTable
                        title="GMAIL SUMMARY"
                        exportName={`${stub}-gmail-summary`}
                        keyField="adgroupName"
                        tableType="gmail"
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        hasData={hasGmailSummaryData}
                        data={gmailSummaryData} />
                </SubModule>
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    SEM
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                <NoChannelDataTile channel='SEM' />
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityMeta: state.entities.meta,
    layerThreeChannel: state.channel.sem,
    keywordSummary: state.sem.keyword.summary,
    adgroupSummary: state.sem.adgroup.summary,
    campaignSummary: state.sem.campaign.summary,
    conversionSummary: state.sem.conversion.summary,
    selectedTimeRange: state.config.selectedTimeRange,
    showSemCpconv: get(state, 'entities.meta.meta.showSemCpconv', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    layerTwo: state.layerTwo
});

export default connect(mapStateToProps, {
    fetchEntityMeta,
    fetchChannel,
    fetchChannelCallouts,
    fetchSemKeywordSummary,
    fetchSemAdgroupSummary,
    fetchSemCampaignSummary,
    fetchSemConversionSummary,
    fetchLayerTwoSEMSummary,
})(LayerTwoSEMContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerTwoSEMContainer.js